// src/DiretorPage.jsx
//
// Página pública do Diretor Técnico — acessível em /diretor sem login.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import { logUxEvent } from './telemetry/uxLogger';

const CFM_BUSCA_URL = 'https://portal.cfm.org.br/busca-medicos';
const HERO_IMAGE_URL = 'https://consultamais.app/img/heros/hero-diretor-16x9.webp';

const DIRETOR_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Physician',
  '@id': 'https://consultamais.app/diretor#diretor-tecnico',
  name: 'Dr. Guilherme Esmanhotto',
  jobTitle: 'Diretor Técnico e médico responsável pelos atendimentos da Consulta+',
  identifier: 'CRM 23.706/DF',
  image: HERO_IMAGE_URL,
  url: 'https://consultamais.app/diretor',
  alumniOf: {
    '@type': 'CollegeOrUniversity',
    name: 'Universidade Federal do Paraná (UFPR)',
  },
  worksFor: {
    '@type': 'MedicalOrganization',
    '@id': 'https://consultamais.app/#org',
    name: 'Consulta+',
  },
  description:
    'Médico formado pela UFPR (2013), CRM 23.706/DF. Formação complementar em radiologia, neurologia, toxicologia, clínica médica e gastroenterologia. Supervisiona a conformidade clínica e dos documentos emitidos na Consulta+.',
  knowsAbout: [
    'Radiologia',
    'Neurologia',
    'Toxicologia',
    'Clínica médica',
    'Gastroenterologia',
    'Teleconsulta',
  ],
  medicalSpecialty: 'GeneralPractice',
  knowsLanguage: ['Portuguese', 'pt-BR'],
};

const PAGE_CSS = `
  .diretor-page__credentials {
    text-align: center;
    margin: 0 0 1.25rem;
    font-size: 0.88rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .diretor-page__credentials strong {
    color: #374151;
  }

  /* CTA primário — mesmo visual do botão da home */
  .diretor-page__cta-wrap {
    display: flex;
    justify-content: center;
    margin: 1.75rem 0 0.5rem;
  }
  .diretor-page__cta {
    background: #D32F2F;
    color: #fff;
    border: none;
    border-radius: 999px;
    font-family: inherit;
    font-size: 1.05rem;
    font-weight: 600;
    padding: 1rem 2rem;
    min-width: min(280px, 100%);
    min-height: 48px;
    cursor: pointer;
    box-shadow: 0 12px 28px rgba(211, 47, 47, 0.18);
    transition: background 0.18s ease, transform 0.18s ease;
    -webkit-tap-highlight-color: transparent;
  }
  .diretor-page__cta:hover { background: #B71C1C; }
  .diretor-page__cta:active { transform: scale(0.98); }
  .diretor-page__cta:focus-visible {
    outline: 2px solid #B71C1C;
    outline-offset: 3px;
  }
`;

const DiretorPage = () => {
  const navigate = useNavigate();

  return (
  <>
    <PageSeo
      path="/diretor"
      title="Diretor Técnico Dr. Guilherme Esmanhotto — CRM 23.706/DF"
      description="Diretor Técnico e médico responsável da Consulta+: Dr. Guilherme Esmanhotto, CRM 23.706/DF, formado em Medicina pela UFPR (2013). Supervisão clínica das teleconsultas."
      image={HERO_IMAGE_URL}
      breadcrumbs={[
        { name: 'Início', url: '/' },
        { name: 'Diretor Técnico', url: '/diretor' },
      ]}
      jsonLd={DIRETOR_JSONLD}
    />
    <style>{PAGE_CSS}</style>
    <LegalPageShell
      eyebrow="Diretoria Técnica"
      title="Dr. Guilherme Esmanhotto"
      subtitle="Diretor Técnico e médico responsável pelos atendimentos da Consulta+"
      contactEmail="atendimento@consultamais.org"
      contactPrefix="Para contato institucional, escreva para"
      heroImage="/img/heros/hero-diretor-16x9.webp"
      heroAlt="Pintura modernista de um médico conferindo com caneta dourada um quadro de checklist com marcas verdes, ao lado de balança e caduceu"
    >
      <p className="diretor-page__credentials">
        CRM <strong>23.706/DF</strong> · Medicina, UFPR (2013)
        {' · '}
        <a href={CFM_BUSCA_URL} target="_blank" rel="noopener noreferrer">
          Verificar no CFM
        </a>
        <br />
        <a
          href="/conteudo/crm-23706-df"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            logUxEvent('diretor_crm_df_click', { target: 'crm-23706-df' });
            navigate('/conteudo/crm-23706-df', { state: { backTo: '/diretor' } });
          }}
        >
          Como conferir o CRM 23.706/DF no portal do CFM
        </a>
      </p>

      <p>
        Dr. <strong>Guilherme Esmanhotto</strong> é o <strong>Diretor Técnico</strong> e{' '}
        <strong>médico responsável pelos atendimentos</strong> da Consulta+. Médico formado pela{' '}
        <strong>Universidade Federal do Paraná (UFPR)</strong> em <strong>2013</strong>, com CRM{' '}
        <strong>23.706/DF</strong>, conduz a supervisão clínica da plataforma e garante que cada
        consulta siga os padrões éticos e clínicos exigidos pelo Conselho Federal de Medicina e
        pela legislação brasileira de telemedicina.
      </p>

      <p>
        Sua formação inclui participação em congressos e jornadas científicas, além de cursos
        complementares em áreas como <strong>radiologia</strong>, <strong>neurologia</strong>,{' '}
        <strong>toxicologia</strong>, <strong>clínica médica</strong> e{' '}
        <strong>gastroenterologia</strong>. Ainda na graduação, foi{' '}
        <strong>coautor de trabalho apresentado</strong> no{' '}
        <strong>42º Congresso Brasileiro de Ortopedia e Traumatologia</strong>, posteriormente
        publicado na <strong>Revista Brasileira de Ortopedia</strong> (2013).
      </p>

      <p>
        Na Consulta+, o Dr. Guilherme Esmanhotto é responsável pela supervisão clínica do
        atendimento, pela supervisão das condutas médicas e pela conformidade dos documentos emitidos
        — receitas, atestados e pedidos de exame — com as normas vigentes. Seu papel é assegurar
        que o paciente seja atendido por médico registrado, com critério clínico e transparência em
        cada etapa do cuidado.
      </p>

      <p style={{ fontSize: '0.88rem', color: '#6B7280' }}>
        <strong>Pessoa jurídica:</strong> <strong>CONSULTA MAIS TELEMEDICINA</strong>,{' '}
        <strong>CNPJ 57.946.158/0001-76</strong>, com sede na Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530. Não confundir
        com a empresa homônima CONSULTAMAIS do setor de análise de crédito —{' '}
        <a href="/consulta-mais-e-consulta-plus">ver desambiguação</a>.
        Ficha para imprensa e citação:{' '}
        <a href="/conteudo/imprensa">consultamais.app/conteudo/imprensa</a>.
      </p>

      <div className="diretor-page__cta-wrap">
        <button
          type="button"
          className="diretor-page__cta"
          onClick={() => {
            logUxEvent('diretor_cta_click', { target: 'consulta' });
            navigate('/consulta');
          }}
        >
          Peça sua consulta agora
        </button>
      </div>
    </LegalPageShell>
  </>
  );
};

export default DiretorPage;
